import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Mail, MapPin, Monitor, Smartphone, Tablet,
  Globe, Clock, TrendingUp, ExternalLink, Target,
  Loader2, FileText, Shield
} from 'lucide-react';

const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatRelative = (dateStr) => {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffM = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffM < 1) return 'just now';
  if (diffM < 60) return `${diffM}m ago`;
  if (diffH < 24) return `${diffH}h ago`;
  if (diffD < 30) return `${diffD}d ago`;
  return '';
};

const WebTrackingVisitorDetail = ({ visitorEmail, history, loading, onBack, onDeleteData, canManage }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-500">Loading visit history...</span>
      </div>
    );
  }

  if (!history) {
    return (
      <div className="text-center py-16">
        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No visit data available for this visitor</p>
      </div>
    );
  }

  const visits = history.visits || [];
  const summary = history.summary || {};

  // Group visits by session
  const sessions = {};
  visits.forEach(v => {
    const sid = v.session_id || 'unknown';
    if (!sessions[sid]) sessions[sid] = [];
    sessions[sid].push(v);
  });
  const sessionEntries = Object.entries(sessions).sort((a, b) => {
    const aFirst = a[1][0]?.visited_at || '';
    const bFirst = b[1][0]?.visited_at || '';
    return new Date(bFirst) - new Date(aFirst);
  });

  return (
    <div className="space-y-6">
      {/* Back Button + Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Visitor Detail</h3>
          <div className="flex items-center gap-2 mt-0.5">
            <Mail className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-sm text-gray-600">{visitorEmail}</span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{summary.total_visits || visits.length}</p>
          <p className="text-xs text-gray-500">Total Visits</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{summary.unique_pages || 0}</p>
          <p className="text-xs text-gray-500">Unique Pages</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{Object.keys(sessions).length}</p>
          <p className="text-xs text-gray-500">Sessions</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">+{summary.total_score || 0}</p>
          <p className="text-xs text-gray-500">Score Earned</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-sm font-medium text-gray-900">{summary.most_common_country || '—'}</p>
          <p className="text-xs text-gray-500">Country</p>
        </div>
      </div>

      {/* Visit Timeline grouped by session */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Visit Timeline</h4>

        {sessionEntries.length === 0 ? (
          <div className="text-center py-8 bg-white border border-gray-200 rounded-xl">
            <p className="text-gray-500">No visits recorded</p>
          </div>
        ) : (
          sessionEntries.map(([sessionId, sessionVisits]) => {
            const firstVisit = sessionVisits[sessionVisits.length - 1];
            const DeviceIcon = DEVICE_ICON[firstVisit?.device_type] || Monitor;

            return (
              <motion.div
                key={sessionId}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
              >
                {/* Session header */}
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DeviceIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">
                      Session &middot; {sessionVisits.length} page{sessionVisits.length !== 1 ? 's' : ''}
                    </span>
                    {firstVisit?.country && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="w-3 h-3" />
                        {firstVisit.country}
                      </span>
                    )}
                    {firstVisit?.campaign_name && (
                      <span className="flex items-center gap-1 text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded">
                        <Target className="w-3 h-3" />
                        {firstVisit.campaign_name}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {formatDateTime(firstVisit?.visited_at)}
                  </span>
                </div>

                {/* Visit list */}
                <div className="divide-y divide-gray-50">
                  {sessionVisits.map((visit, idx) => (
                    <div key={visit.id || idx} className="px-5 py-3 flex items-center gap-4">
                      {/* Timeline dot */}
                      <div className="relative flex flex-col items-center">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          idx === 0 ? 'bg-primary-500' : 'bg-gray-300'
                        }`} />
                        {idx < sessionVisits.length - 1 && (
                          <div className="w-px h-6 bg-gray-200 absolute top-3" />
                        )}
                      </div>

                      {/* Page info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {visit.page_title || visit.page_url}
                          </span>
                          {visit.page_url && (
                            <a
                              href={visit.page_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-400 hover:text-primary-600 flex-shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-0.5">{visit.page_url}</p>
                        {visit.referrer && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            from: {visit.referrer}
                          </p>
                        )}
                      </div>

                      {/* Score + time */}
                      <div className="text-right flex-shrink-0">
                        {visit.score_delta > 0 && (
                          <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
                            <TrendingUp className="w-3 h-3" />
                            +{visit.score_delta}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                          <Clock className="w-3 h-3" />
                          {formatRelative(visit.visited_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* GDPR: Delete visitor data */}
      {canManage && onDeleteData && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="font-medium text-red-900 text-sm">GDPR: Right to Erasure</h5>
              <p className="text-xs text-red-700 mt-1">
                Remove all tracking data (tokens + visits) for this visitor across all sites.
              </p>
              <button
                onClick={() => {
                  if (window.confirm(`Permanently delete all tracking data for ${visitorEmail}? This cannot be undone.`)) {
                    onDeleteData(visitorEmail);
                  }
                }}
                className="mt-2 px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete All Data for This Visitor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebTrackingVisitorDetail;
